import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ICompanyWiseBookingReport,
  ICustomerList,
  IHotelBlockRoomStatusReport,
  IPaymentReceiptLedger,
  IReportInitialState,
  ITourBookingInvoice,
  ITourReport,
  IUserWiseTourBookingReport,
} from "./reportModel";
import {
  getAirlineBlockBookingExcelSheetData,
  getAirlineBlockFlights,
  GetBookingSummaryReport,
  getCompanyWiseBookingReport,
  getCustomerListReport,
  GetCustomerRetentionReport,
  GetDailyPerformanceSnapshotReport,
  GetDestinationWisePerformanceReport,
  getFITBookingReport,
  GetFollowupEfficiencyReport,
  GetGSTAndTaxSummaryReport,
  getHotelBlockRoomStatus,
  GetInquiryConversionReport,
  getInquiryReport,
  getLeadReport,
  GetLeadSourceAnalysisReport,
  getLedgerReport,
  GetLostInquiryAnalysisReport,
  GetMonthlyRevenueTrendReport,
  getPaymentReceiptCustomerView,
  GetPendingInquiryFollowupReport,
  GetProfitabilityReport,
  GetPromocodeUsageReport,
  getQuotationReport,
  GetQuotationStatusReport,
  GetRevenueGeneratedByUserReport,
  GetServiceTypeRevenueReport,
  getTourBookingInvoice,
  getTourBookingReport,
  getTourReport,
  GetUpcomingDepartureDataReport,
  GetUserPerformanceReport,
  getUserWiseTourBookingReport,
} from "./services/reports.services";

const initialState: IReportInitialState = {
  loading: false,
  listLoading: false,
  error: "",
  CompanyWiseBookingReport: [],
  CompanyWiseBookingReportDetails: {
    CompanyID: null,
    FromDate: "",
    ToDate: "",
    DateType: null,
    Type: null,
  },
  UserWiseTourBookingReport: [],
  UserWiseTourBookingReportDetails: {
    FromDate: "",
    ToDate: "",
    BranchID: null,
    UserID: null,
  },
  HotelBlockRoomStatusReport: [],
  CustomerList: [],
  CustomerDetails: {
    DateType: null,
    FromDate: "",
    ToDate: "",
  },
  TourBookingReportDetails: {
    TourBookingNo: null,
  },
  PaymentReceiptCustomerDetails: {
    PaymentReceiptNo: "",
  },
  TourReport: [],
  TourBookingInvoice: {
    TourBookingNo: "",
  },
  AirlineBlockFlightList: [],
  AirlineBlockViewDetails: {
    DepartureAirportID: null,
    ArrivalAirportID: null,
    DepartureDate: "string",
  },
  AirlineBlockExcelData: {},
  PaymentReceiptLedger: [],
  LeadListForReport: [],
  InquiryListForReport: [],
  QuotationListForReport: [],
  FITBookingListForReport: [],
  BookingSummaryListForReport: [],
  RevenueGeneratedListForReport: [],
  InquiryConversionListForReport: [],
  destinationwiseperformanceListForReport: [],
  LeadSourceAnalysisListForReport: [],
  UserPerformanceListForReport: [],
  QuotationStatusListForReport: [],
  ProfitabilityListForReport: [],
  PendingInquiryFollowupListForReport: [],
  LostInquiryAnalysisListForReport: [],
  MonthlyRevenueTrendListForReport: [],
  UpcomingDepartureDataListForReport: [],
  CustomerRetentionListForReport: [],
  GSTAndTaxSummaryListForReport: [],
  ServiceTypeRevenueListForReport: [],
  FollowupEfficiencyListForReport: [],
  PromocodeUsageListForReport: [],
  DailyPerformanceSnapshotListForReport: [],
};

const reportSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    setCompanyWiseBookingReport: (state, action) => {
      state.CompanyWiseBookingReportDetails = action.payload;
    },
    clearCompanyWiseBookingReportList: (state) => {
      state.CompanyWiseBookingReport = [];
    },
    setUserWiseTourBookingReport: (state, action) => {
      state.UserWiseTourBookingReportDetails = action.payload;
    },
    clearUserWiseTourBookingReportList: (state) => {
      state.UserWiseTourBookingReport = [];
    },
    clearHotelBlockRoomStatusReportList: (state) => {
      state.HotelBlockRoomStatusReport = [];
    },
    setCustomerDetails: (state, action) => {
      state.CustomerDetails = action.payload;
    },
    clearCustomerList: (state) => {
      state.CustomerList = [];
    },
    clearTourReportList: (state) => {
      state.TourReport = [];
    },
    clearLeadReportList: (state) => {
      state.LeadListForReport = [];
    },
    setAirlineBlockViewDetails: (state, action) => {
      state.AirlineBlockViewDetails = action.payload;
    },
    clearAirlineBlockFlightList: (state) => {
      state.AirlineBlockFlightList = [];
    },
    clearPaymentReceiptLedgerList: (state) => {
      state.PaymentReceiptLedger = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCompanyWiseBookingReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getCompanyWiseBookingReport.fulfilled,
      (state, action: PayloadAction<ICompanyWiseBookingReport[]>) => {
        state.loading = false;
        state.CompanyWiseBookingReport = action.payload || [];
      },
    );
    builder.addCase(getCompanyWiseBookingReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.CompanyWiseBookingReport = [];
    });
    builder.addCase(getUserWiseTourBookingReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getUserWiseTourBookingReport.fulfilled,
      (state, action: PayloadAction<IUserWiseTourBookingReport[]>) => {
        state.loading = false;
        state.UserWiseTourBookingReport = action.payload || [];
      },
    );
    builder.addCase(getUserWiseTourBookingReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.UserWiseTourBookingReport = [];
    });
    builder.addCase(getHotelBlockRoomStatus.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getHotelBlockRoomStatus.fulfilled,
      (state, action: PayloadAction<IHotelBlockRoomStatusReport[]>) => {
        state.loading = false;
        state.HotelBlockRoomStatusReport = action.payload || [];
      },
    );
    builder.addCase(getHotelBlockRoomStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.HotelBlockRoomStatusReport = [];
    });
    builder.addCase(getCustomerListReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getCustomerListReport.fulfilled,
      (state, action: PayloadAction<ICustomerList[]>) => {
        state.loading = false;
        state.CustomerList = action.payload || [];
      },
    );
    builder.addCase(getCustomerListReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.CustomerList = [];
    });
    builder.addCase(getTourBookingReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getTourBookingReport.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.TourBookingReportDetails = action.payload;
      },
    );
    builder.addCase(getTourBookingReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.TourBookingReportDetails = initialState.TourBookingReportDetails;
    });
    builder.addCase(getPaymentReceiptCustomerView.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getPaymentReceiptCustomerView.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.PaymentReceiptCustomerDetails = action.payload;
      },
    );
    builder.addCase(getPaymentReceiptCustomerView.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.PaymentReceiptCustomerDetails =
        initialState.PaymentReceiptCustomerDetails;
    });
    builder.addCase(getTourReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getTourReport.fulfilled,
      (state, action: PayloadAction<ITourReport[]>) => {
        state.loading = false;
        state.TourReport = action.payload || [];
      },
    );
    builder.addCase(getTourReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.TourReport = [];
    });
    builder.addCase(getTourBookingInvoice.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getTourBookingInvoice.fulfilled,
      (state, action: PayloadAction<ITourBookingInvoice>) => {
        state.loading = false;
        state.TourBookingInvoice = action.payload;
      },
    );
    builder.addCase(getTourBookingInvoice.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.TourBookingInvoice = initialState.TourBookingInvoice;
    });
    builder.addCase(getAirlineBlockFlights.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAirlineBlockFlights.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.AirlineBlockFlightList = action.payload || [];
      },
    );
    builder.addCase(getAirlineBlockFlights.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.AirlineBlockFlightList = [];
    });
    builder.addCase(getAirlineBlockBookingExcelSheetData.pending, (state) => {
      state.listLoading = true;
      state.error = "";
    });
    builder.addCase(
      getAirlineBlockBookingExcelSheetData.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.listLoading = false;
        state.AirlineBlockExcelData = action.payload;
      },
    );
    builder.addCase(
      getAirlineBlockBookingExcelSheetData.rejected,
      (state, action) => {
        state.listLoading = false;
        state.error = action.error.message || "Something went wrong";
        state.AirlineBlockExcelData = initialState.AirlineBlockExcelData;
      },
    );
    builder.addCase(getLedgerReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getLedgerReport.fulfilled,
      (state, action: PayloadAction<IPaymentReceiptLedger[]>) => {
        state.loading = false;
        state.PaymentReceiptLedger = action.payload || [];
      },
    );
    builder.addCase(getLedgerReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.PaymentReceiptLedger = [];
    });
    builder.addCase(getLeadReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getLeadReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.LeadListForReport = action.payload || [];
      },
    );
    builder.addCase(getLeadReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.LeadListForReport = [];
    });
    builder.addCase(getInquiryReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getInquiryReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.InquiryListForReport = action.payload || [];
      },
    );
    builder.addCase(getInquiryReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.InquiryListForReport = [];
    });
    builder.addCase(getQuotationReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getQuotationReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.QuotationListForReport = action.payload || [];
      },
    );
    builder.addCase(getQuotationReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.QuotationListForReport = [];
    });
    builder.addCase(getFITBookingReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getFITBookingReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.FITBookingListForReport = action.payload || [];
      },
    );
    builder.addCase(getFITBookingReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.FITBookingListForReport = [];
    });

    builder.addCase(GetBookingSummaryReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetBookingSummaryReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.BookingSummaryListForReport = action.payload || [];
      },
    );
    builder.addCase(GetBookingSummaryReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.BookingSummaryListForReport = [];
    });

    builder.addCase(GetRevenueGeneratedByUserReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetRevenueGeneratedByUserReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.RevenueGeneratedListForReport = action.payload || [];
      },
    );
    builder.addCase(
      GetRevenueGeneratedByUserReport.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Something went wrong";
        state.RevenueGeneratedListForReport = [];
      },
    );

    builder.addCase(GetInquiryConversionReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetInquiryConversionReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.InquiryConversionListForReport = action.payload || [];
      },
    );
    builder.addCase(GetInquiryConversionReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.InquiryConversionListForReport = [];
    });

    builder.addCase(GetDestinationWisePerformanceReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetDestinationWisePerformanceReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.destinationwiseperformanceListForReport = action.payload || [];
      },
    );
    builder.addCase(
      GetDestinationWisePerformanceReport.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Something went wrong";
        state.destinationwiseperformanceListForReport = [];
      },
    );

    builder.addCase(GetLeadSourceAnalysisReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetLeadSourceAnalysisReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.LeadSourceAnalysisListForReport = action.payload || [];
      },
    );
    builder.addCase(GetLeadSourceAnalysisReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.LeadSourceAnalysisListForReport = [];
    });

    builder.addCase(GetUserPerformanceReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetUserPerformanceReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.UserPerformanceListForReport = action.payload || [];
      },
    );
    builder.addCase(GetUserPerformanceReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.UserPerformanceListForReport = [];
    });

    builder.addCase(GetQuotationStatusReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetQuotationStatusReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.QuotationStatusListForReport = action.payload || [];
      },
    );
    builder.addCase(GetQuotationStatusReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.QuotationStatusListForReport = [];
    });

    builder.addCase(GetProfitabilityReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetProfitabilityReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.ProfitabilityListForReport = action.payload || [];
      },
    );
    builder.addCase(GetProfitabilityReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ProfitabilityListForReport = [];
    });

    builder.addCase(GetPendingInquiryFollowupReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetPendingInquiryFollowupReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.PendingInquiryFollowupListForReport = action.payload || [];
      },
    );
    builder.addCase(
      GetPendingInquiryFollowupReport.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Something went wrong";
        state.PendingInquiryFollowupListForReport = [];
      },
    );

    builder.addCase(GetLostInquiryAnalysisReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetLostInquiryAnalysisReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.LostInquiryAnalysisListForReport = action.payload || [];
      },
    );
    builder.addCase(GetLostInquiryAnalysisReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.LostInquiryAnalysisListForReport = [];
    });

    builder.addCase(GetMonthlyRevenueTrendReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetMonthlyRevenueTrendReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.MonthlyRevenueTrendListForReport = action.payload || [];
      },
    );
    builder.addCase(GetMonthlyRevenueTrendReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.MonthlyRevenueTrendListForReport = [];
    });

    builder.addCase(GetUpcomingDepartureDataReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetUpcomingDepartureDataReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.UpcomingDepartureDataListForReport = action.payload || [];
      },
    );
    builder.addCase(
      GetUpcomingDepartureDataReport.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Something went wrong";
        state.UpcomingDepartureDataListForReport = [];
      },
    );

    builder.addCase(GetCustomerRetentionReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetCustomerRetentionReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.CustomerRetentionListForReport = action.payload || [];
      },
    );
    builder.addCase(GetCustomerRetentionReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.CustomerRetentionListForReport = [];
    });

    builder.addCase(GetGSTAndTaxSummaryReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetGSTAndTaxSummaryReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.GSTAndTaxSummaryListForReport = action.payload || [];
      },
    );
    builder.addCase(GetGSTAndTaxSummaryReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.GSTAndTaxSummaryListForReport = [];
    });

    builder.addCase(GetServiceTypeRevenueReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetServiceTypeRevenueReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.ServiceTypeRevenueListForReport = action.payload || [];
      },
    );
    builder.addCase(GetServiceTypeRevenueReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.ServiceTypeRevenueListForReport = [];
    });

    builder.addCase(GetFollowupEfficiencyReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetFollowupEfficiencyReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.FollowupEfficiencyListForReport = action.payload || [];
      },
    );
    builder.addCase(GetFollowupEfficiencyReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.FollowupEfficiencyListForReport = [];
    });

    builder.addCase(GetDailyPerformanceSnapshotReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetDailyPerformanceSnapshotReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.DailyPerformanceSnapshotListForReport = action.payload || [];
      },
    );
    builder.addCase(
      GetDailyPerformanceSnapshotReport.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Something went wrong";
        state.DailyPerformanceSnapshotListForReport = [];
      },
    );

    builder.addCase(GetPromocodeUsageReport.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      GetPromocodeUsageReport.fulfilled,
      (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.PromocodeUsageListForReport = action.payload || [];
      },
    );
    builder.addCase(GetPromocodeUsageReport.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.PromocodeUsageListForReport = [];
    });
  },
});

export const {
  setCompanyWiseBookingReport,
  clearCompanyWiseBookingReportList,
  setUserWiseTourBookingReport,
  clearUserWiseTourBookingReportList,
  clearHotelBlockRoomStatusReportList,
  setCustomerDetails,
  clearCustomerList,
  clearTourReportList,
  clearLeadReportList,
  setAirlineBlockViewDetails,
  clearAirlineBlockFlightList,
  clearPaymentReceiptLedgerList,
} = reportSlice.actions;
export default reportSlice.reducer;
